import {
  formatPhoneNumberIntl,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input'

/**
 * Checks if a given string is a valid phone number.
 *
 * @param {string} str the string to be checked
 * @return {boolean} true if the string is a valid phone number
 */
export const isNumber = (str: string): boolean => {
  /**
   * The regular expression checks for strings containing only digits, the plus sign, and spaces.
   */
  return /^[0-9+]+$/.test(str)
}

/**
 * Formats a phone number string with spaces.
 *
 * @param {string} value the phone number string to format
 * @param {number} length the length of the phone number string
 * @return {string} the formatted phone number string
 */
export const numberFormatter = (
  value: string = '',
  length: number = value?.length,
): string => {
  if (!value && length < value?.length) {
    return ''
  }
  const regExp = new RegExp(`^(\\d{3})(\\d{3})(\\d{0,${length}})`)
  return value.replace(regExp, '$1 $2 $3')
}

/**
 * Formats a given phone number string into an international format or a custom format.
 *
 * The function first checks for invalid characters in the input number.
 * If the number contains invalid characters, it returns the original number.
 * It attempts to parse the number and formats it using the national number format.
 * If the phone number is valid and possible, it formats it into the international format.
 * Otherwise, it returns the parsed number with the country calling code.
 *
 * @param {string} [number] - The phone number to be formatted.
 * @return {string | undefined} The formatted phone number or the original number if invalid.
 */
export const customPhoneNumberFormatter = (
  number?: string,
): string | undefined => {
  const pattern = /[a-zA-Z*#]/g
  if (!number) return

  // Check for invalid characters in the phone number
  const patternMatchResult = number.match(pattern)
  if (patternMatchResult && patternMatchResult.length > 0) {
    return number
  }

  const noLength = number.length
  const parsedNumber = parsePhoneNumber(number)
  const result = numberFormatter(String(parsedNumber?.nationalNumber), noLength)

  // Format the number with the country calling code
  const formattedNumber = parsedNumber
    ? '+' + parsedNumber.countryCallingCode + ' ' + result
    : number

  // Return the international format if valid, otherwise return the custom format
  return isPossiblePhoneNumber(number) && isValidPhoneNumber(number)
    ? formatPhoneNumberIntl(number)
    : formattedNumber
}

/**
 * Checks if the given string is a valid phone number with spaces.
 * @param {string} str the string to be checked
 * @return {boolean} true if the string is a valid phone number with spaces
 */
export const isNumberWithSpaces = (str: string): boolean => {
  // The regular expression checks for strings containing only digits, the plus
  // sign, and spaces.
  return /^[0-9+\s]+$/.test(str)
}

/**
 * Formats a phone number string with spaces.
 *
 * The function formats a phone number string with spaces so that it
 * can be used in a keypad input field.
 *
 * @param {string} value the phone number string to format
 * @return {string} the formatted phone number string
 */
export const numberFormatterForKeypad = (value: string): string => {
  if (value.length <= 3) {
    return value // return the first 3 digits
  } else if (value.length <= 6) {
    return value.slice(0, 3) + ' ' + value.slice(3) // return the first 3 digits and the rest after a space
  } else {
    return value.slice(0, 3) + ' ' + value.slice(3, 6) + ' ' + value.slice(6) // return the first 3 digits, then the next 3 digits after a space, and the rest after another space
  }
}
